import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import NewNeedModal from "../components/NewNeedModal";
import NewRecruitmentModal from "../components/NewRecruitmentModal";
import ChatInterview from "../components/ChatInterview";
import VoiceAgent from "../components/VoiceAgent";
import { useNavigate } from "react-router-dom";
import {
  Plus, Briefcase, CalendarDays, AlertTriangle, Star, Bell, BellRing,
  HelpCircle, ChevronRight, FileText, CheckCircle2, Mic, MessageSquare,
} from "lucide-react";

export default function PharmacyDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);            // new manual wizard
  const [recruitOpen, setRecruitOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);    // chat interview
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/dashboard/stats");
    setStats(data);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-6 lg:p-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
                {t("dashboard.title")} <span className="text-2xl">👋</span>
              </h1>
              <p className="text-sm text-[#6B726A] mt-1">{t("dashboard.subtitle")}</p>
            </div>
            <div className="flex items-center gap-3">
              <button data-testid="alerts-bell" onClick={() => nav("/alerts")} className="relative w-10 h-10 grid place-items-center rounded-full bg-white border border-[#E2E5E0] hover:border-[#1A4D2E]/30">
                <Bell className="w-4 h-4 text-[#1A4D2E]" />
                {stats?.alerts > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#E3655B] text-white text-[10px] rounded-full px-1.5 py-0.5">
                    {stats.alerts}
                  </span>
                )}
              </button>
              <button className="w-10 h-10 grid place-items-center rounded-full bg-white border border-[#E2E5E0]">
                <HelpCircle className="w-4 h-4 text-[#1A4D2E]" />
              </button>
              <button onClick={() => setChatOpen(true)} data-testid="chat-need-button" title="Décrire au chat"
                className="w-10 h-10 grid place-items-center rounded-full bg-white border border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F0EA] transition-colors">
                <MessageSquare className="w-4 h-4" />
              </button>
              <button onClick={() => setVoiceOpen(true)} data-testid="voice-need-button" title="Décrire à l'oral"
                className="w-10 h-10 grid place-items-center rounded-full bg-white border border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F0EA] transition-colors">
                <Mic className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(true)} data-testid="new-need-button" className="rp-btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" /> {t("dashboard.new_need")}
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            <StatCard testid="stat-in-progress" icon={Briefcase} tint="#1A4D2E" tintBg="#E8F0EA"
              label="Mission en cours" value={stats?.in_progress ?? "—"} hint="Aujourd'hui"
              onClick={() => nav("/bookings")} />
            <StatCard testid="stat-upcoming" icon={CalendarDays} tint="#2F855A" tintBg="#E0F1E5"
              label="Besoins à venir" value={stats?.upcoming ?? "—"} hint="Dans les 30 prochains jours"
              onClick={() => nav("/needs")} />
            <StatCard testid="stat-alerts" icon={AlertTriangle} tint="#C97A20" tintBg="#FBEFDA"
              label="Alertes" value={stats?.alerts ?? "—"} hint="Risque de sous-effectif"
              onClick={() => nav("/alerts")} />
            <StatCard testid="stat-favorites" icon={Star} tint="#3B5BC2" tintBg="#E7ECFB"
              label="Candidats favoris" value={stats?.favorites ?? "—"} hint="Profils enregistrés"
              onClick={() => nav("/replacements")} />
          </div>

          {(stats?.drafts > 0 || stats?.stale > 0) && (
            <div className="rp-card p-5 mb-6 border-l-4 border-l-[#C97A20] bg-[#FFFBF2]" data-testid="dashboard-engagement-nudge">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FBEFDA] grid place-items-center shrink-0">
                  <FileText className="w-5 h-5 text-[#C97A20]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm" style={{ fontFamily: "Outfit" }}>Quelques besoins demandent votre attention</h4>
                  <p className="text-xs text-[#6B726A] mt-1">
                    {stats.drafts > 0 && <><strong>{stats.drafts}</strong> besoin{stats.drafts > 1 ? "s" : ""} en brouillon</>}
                    {stats.drafts > 0 && stats.stale > 0 && " · "}
                    {stats.stale > 0 && <><strong>{stats.stale}</strong> publié{stats.stale > 1 ? "s" : ""} sans candidat depuis +48 h</>}
                  </p>
                  <button onClick={() => nav("/needs")} data-testid="nudge-cta"
                    className="mt-3 text-xs font-medium text-[#C97A20] hover:underline inline-flex items-center gap-1">
                    Examiner les besoins →
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-5 mb-6">
            <div className="rp-card p-6">
              <h3 className="font-medium text-base mb-4" style={{ fontFamily: "Outfit" }}>{t("dashboard.next_alert")}</h3>
              {stats?.alerts > 0 ? (
                <div className="bg-[#FFF4F3] border border-[#E3655B]/30 rounded-2xl p-5">
                  <div className="flex items-center gap-2 text-[#E3655B] font-medium text-sm mb-2">
                    <AlertTriangle className="w-4 h-4" /> {t("dashboard.unread_alerts", { count: stats.alerts })}
                  </div>
                  <p className="text-sm text-[#1f2421] mb-4">
                    {t("dashboard.have_notifications")}
                  </p>
                  <button onClick={() => nav("/alerts")} className="text-sm font-medium text-[#E3655B] underline-offset-2 hover:underline" data-testid="alert-cta">
                    {t("dashboard.see_recommendations")}
                  </button>
                </div>
              ) : (
                <div className="bg-[#E8F0EA] border border-[#1A4D2E]/15 rounded-2xl p-5">
                  <div className="flex items-center gap-2 text-[#1A4D2E] font-medium text-sm mb-2">
                    <CheckCircle2 className="w-4 h-4" /> {t("dashboard.all_calm")}
                  </div>
                  <p className="text-sm text-[#1f2421] mb-4">
                    {t("dashboard.no_alerts")}
                  </p>
                  <button onClick={() => setOpen(true)} className="text-sm font-medium text-[#1A4D2E] underline-offset-2 hover:underline" data-testid="alert-cta">
                    {t("dashboard.anticipate")}
                  </button>
                </div>
              )}
            </div>

            <div className="rp-card p-6">
              <h3 className="font-medium text-base mb-4" style={{ fontFamily: "Outfit" }}>{t("dashboard.next_booking")}</h3>
              {stats?.next_booking ? (
                <button
                  onClick={() => nav("/bookings")}
                  data-testid="next-booking-card"
                  className="w-full text-left -m-2 p-2 rounded-2xl hover:bg-[#F7F8F6] transition-colors group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-[#E8F0EA] grid place-items-center text-[#1A4D2E] font-medium">
                      {(stats.next_booking.replacement_name || "?")[0]}
                    </div>
                    <div>
                      <div className="font-medium">{stats.next_booking.replacement_name}</div>
                      <div className="text-xs text-[#6B726A]">{stats.next_booking.need_snapshot?.role_type}</div>
                    </div>
                    <span className="ml-auto text-xs bg-[#E8F0EA] text-[#1A4D2E] px-2 py-1 rounded-full">{t("dashboard.confirmed")}</span>
                  </div>
                  <div className="text-sm text-[#6B726A] space-y-1">
                    <div>📅 {stats.next_booking.need_snapshot?.start_date}</div>
                    <div>🕘 {stats.next_booking.need_snapshot?.start_time} – {stats.next_booking.need_snapshot?.end_time}</div>
                  </div>
                  <div className="text-sm font-medium text-[#1A4D2E] mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t("common.see_detail")} <ChevronRight className="w-4 h-4" />
                  </div>
                </button>
              ) : (
                <EmptyTiny label={t("dashboard.no_booking")} />
              )}
            </div>

            <div className="rp-card p-6">
              <h3 className="font-medium text-base mb-4" style={{ fontFamily: "Outfit" }}>{t("dashboard.recent_activity")}</h3>
              {stats?.recent_activity?.length ? (
                <ul className="space-y-1">
                  {stats.recent_activity.slice(0, 4).map((a) => (
                    <li key={a.id}>
                      <button
                        onClick={() => nav("/alerts")}
                        data-testid={`activity-${a.id}`}
                        className="w-full flex gap-3 items-start text-left p-2 -m-2 rounded-xl hover:bg-[#F7F8F6] transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#E8F0EA] grid place-items-center text-[#1A4D2E] shrink-0 mt-0.5">
                          {a.type === "booking_response" ? <CheckCircle2 className="w-4 h-4" /> :
                            a.type === "urgent_search" ? <BellRing className="w-4 h-4" /> :
                            <FileText className="w-4 h-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{a.title}</div>
                          <div className="text-xs text-[#6B726A] truncate">{a.message}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#6B726A] opacity-0 group-hover:opacity-100 mt-1" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyTiny label={t("dashboard.no_activity")} />
              )}
            </div>
          </div>

          <div className="rp-card p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FFF4F3] grid place-items-center urgent-pulse">
              <BellRing className="w-5 h-5 text-[#E3655B]" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium" style={{ fontFamily: "Outfit" }}>{t("dashboard.urgent_question")}</h4>
              <p className="text-sm text-[#6B726A]">{t("dashboard.urgent_desc")}</p>
            </div>
            <button onClick={() => setUrgentOpen(true)} data-testid="urgent-search-button"
              className="border border-[#E3655B] text-[#E3655B] hover:bg-[#FFF4F3] rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2">
              <BellRing className="w-4 h-4" /> {t("dashboard.urgent_search")}
            </button>
          </div>
        </div>
      </main>

      <NewNeedModal open={open} onClose={() => setOpen(false)} onCreated={() => load()} defaultAddress={user?.address || ""}
        onSwitchMode={(m) => { if (m === "permanent") { setOpen(false); setRecruitOpen(true); } }} />
      <NewRecruitmentModal open={recruitOpen} onClose={() => setRecruitOpen(false)} onCreated={() => load()}
        onSwitchMode={(m) => { if (m === "temporary") { setRecruitOpen(false); setOpen(true); } }} />
      <ChatInterview
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        title="Nouveau besoin de candidat"
        introMessage="Bonjour ! Je vais vous poser quelques questions rapides pour créer votre besoin en moins d'une minute. C'est parti 🚀"
        submitLabel="Publier le besoin"
        questions={[
          { key: "start_date", type: "date", question: "Pour quelle date avez-vous besoin d'un candidat ?" },
          { key: "end_date", type: "date", question: "Et jusqu'à quelle date ? (mettez la même si c'est une seule journée)" },
          { key: "start_time", type: "time", question: "Quelle heure de début ? (ex: 09:00)" },
          { key: "end_time", type: "time", question: "Et l'heure de fin ?" },
          { key: "role_type", type: "choice", question: "Quel profil cherchez-vous ?", options: [
            { value: "Pharmacien", label: "Pharmacien" },
            { value: "Adjoint", label: "Adjoint" },
            { value: "Préparateur", label: "Préparateur" },
          ]},
          { key: "skills", type: "multi", question: "Quelles compétences sont importantes pour ce besoin ?", options: [
            "Vaccination", "TROD", "Entretiens", "Orthopédie", "Homéopathie", "Aromathérapie", "PUI",
          ], skippable: true },
          { key: "address", type: "text", question: "Quelle est l'adresse exacte de l'officine ?", placeholder: "12 rue de la République, 75011 Paris" },
          { key: "hourly_rate", type: "number", question: "Quel tarif horaire proposez-vous ? (en €/h)", placeholder: "32" },
          { key: "urgent", type: "boolean", question: "S'agit-il d'une recherche urgente ?" },
          { key: "notes", type: "text", question: "Une dernière note pour les candidats ? (sinon dites « non »)", skippable: true },
        ]}
        onSubmit={async (a) => {
          await api.post("/needs", {
            start_date: a.start_date, end_date: a.end_date,
            start_time: a.start_time || "09:00", end_time: a.end_time || "19:00",
            role_type: a.role_type, skills: a.skills || [],
            address: a.address, urgent: !!a.urgent,
            hourly_rate: parseFloat(a.hourly_rate) || 0,
            notes: a.notes || "",
          });
          load();
        }}
      />
      <UrgentSearchModal open={urgentOpen} onClose={() => setUrgentOpen(false)} onCreated={() => load()} />
      <VoiceAgent
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        context="need"
        onCompleted={() => load()}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, tint, tintBg, testid, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`rp-card p-6 text-left w-full ${onClick ? "cursor-pointer hover:-translate-y-0.5 transition-transform" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-[#6B726A] font-semibold">{label}</span>
        <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: tintBg }}>
          <Icon className="w-4 h-4" style={{ color: tint }} />
        </div>
      </div>
      <div className="text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit", color: tint }}>
        {value}
      </div>
      <div className="text-xs text-[#6B726A] mt-1">{hint}</div>
    </Tag>
  );
}

function EmptyTiny({ label }) {
  return <div className="text-sm text-[#6B726A] py-4 text-center">{label}</div>;
}

// Inline urgent search modal
function UrgentSearchModal({ open, onClose, onCreated }) {
  const [address, setAddress] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const launch = async () => {
    setBusy(true);
    try {
      await api.post("/needs", {
        start_date: date, end_date: date, start_time: "09:00", end_time: "19:00",
        role_type: "Pharmacien", skills: [], address, urgent: true, notes: "Recherche urgente",
      });
      onCreated?.();
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl w-full max-w-md p-8 text-center" data-testid="urgent-modal">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#FFF4F3] grid place-items-center mb-5 urgent-pulse">
          <BellRing className="w-8 h-8 text-[#E3655B]" />
        </div>
        <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Recherche urgente</h3>
        <p className="text-sm text-[#6B726A] mb-6">
          Nous notifions les candidats disponibles dans votre secteur. Vous recevrez les premières réponses en quelques minutes.
        </p>
        <div className="space-y-3 text-left">
          <input type="date" className="w-full bg-white border border-[#E2E5E0] rounded-xl px-4 py-2.5" value={date} onChange={(e) => setDate(e.target.value)} data-testid="urgent-date" />
          <input placeholder="Adresse" className="w-full bg-white border border-[#E2E5E0] rounded-xl px-4 py-2.5" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="urgent-address" />
        </div>
        <button onClick={launch} disabled={busy || !date} data-testid="launch-urgent" className="mt-6 w-full bg-[#E3655B] text-white font-medium rounded-full py-3 hover:bg-[#cf564c] transition-colors disabled:opacity-50">
          Lancer la recherche urgente
        </button>
        <button onClick={onClose} className="block mx-auto mt-3 text-sm text-[#6B726A] hover:text-[#1f2421]">Annuler</button>
      </div>
    </div>
  );
}
